import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, Loader2, RefreshCw, Lock, Unlock, Power, Eye, Zap, Undo2, ExternalLink, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLE = {
  OFF: "bg-[#F1F1F4] text-[#6E6E73]",
  ACTIVE: "bg-[#E9F9EF] text-[#0E8A46]",
  LOCKED: "bg-[#FFF1E6] text-[#B5590F]",
};

const Stat = ({ label, value, tone }) => (
  <div className="rounded-xl bg-white/60 px-3 py-2">
    <p className="num text-[18px] font-bold leading-none" style={tone ? { color: tone } : {}}>{value}</p>
    <p className="mt-1 text-[11px] text-[#8E8E93]">{label}</p>
  </div>
);

const Btn = ({ testid, onClick, disabled, busy, icon: Icon, children, tone = "light" }) => {
  const cls = {
    light: "border border-[#E1E1E6] bg-white/70 text-[#1D1D1F] hover:bg-white",
    blue: "bg-[#0071E3] text-white hover:bg-[#0077ED]",
    green: "bg-[#128C3E] text-white hover:bg-[#0F7433]",
    amber: "bg-[#B5590F] text-white hover:brightness-110",
    red: "bg-[#C8102E] text-white hover:bg-[#A60D26]",
  }[tone];
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled || busy}
      className={`flex h-9 items-center justify-center gap-1.5 rounded-full px-3.5 text-[12.5px] font-medium transition-all active:scale-[0.98] disabled:opacity-50 ${cls}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : Icon && <Icon className="h-3.5 w-3.5" />} {children}
    </button>
  );
};

export default function FingerspotSyncPanel({ periodId, onChanged }) {
  const { readOnly } = useAuth();
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/periods/${periodId}/sync-status`);
      setSt(data);
    } catch { /* silent */ }
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const openPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.get(`/periods/${periodId}/sync-preview`);
      setPreview(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  if (!st) return null;
  const s = st.summary || {};
  const status = st.status;

  return (
    <section data-testid="fp-sync-panel" className="liquid-glass-card mt-4 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Fingerprint className="h-5 w-5 text-[#0071E3]" />
          <h3 className="text-[15px] font-semibold tracking-tight">Fingerprint Attendance Sync</h3>
          <span data-testid="fp-sync-status" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status]}`}>{status}</span>
          {st.auto_activated && (
            <span data-testid="fp-auto-activated-badge" className="rounded-full bg-[#E9F9EF] px-2.5 py-0.5 text-[11px] font-medium text-[#0E8A46]">
              Auto Activated{st.auto_activated_at ? ` · ${new Date(st.auto_activated_at).toLocaleDateString("id-ID")}` : ""}
            </span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-[#8E8E93] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-black/[0.06] px-5 py-4">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            <Stat label="Active" value={s.active_employees ?? 0} />
            <Stat label="Mapped" value={s.mapped_employees ?? 0} />
            <Stat label="Fingerspot Payroll" value={s.fingerspot_payroll ?? 0} tone="#0E8A46" />
            <Stat label="Attendance Only" value={s.attendance_only ?? 0} tone={s.attendance_only ? "#B5590F" : undefined} />
            <Stat label="Manual Payroll" value={s.manual_payroll ?? 0} />
            <Stat label="Approved Days" value={s.approved_days ?? 0} tone="#0058C7" />
            <Stat label="Needs Review" value={s.needs_review ?? 0} tone="#B5590F" />
            <Stat label="Conflicts" value={s.mapping_conflicts ?? 0} tone={s.mapping_conflicts ? "#C8102E" : undefined} />
          </div>

          <p className="text-[11.5px] text-[#8E8E93]">
            Go-live: <span className="num font-medium text-[#6E6E73]">{(st.go_live || "").slice(0, 10)}</span>
            {"  ·  "}Effective from: <span className="num font-medium text-[#6E6E73]">{st.effective_start}</span>
            {st.last_sync && <>{"  ·  "}Last sync: <span className="font-medium text-[#6E6E73]">{new Date(st.last_sync).toLocaleString("id-ID")}</span></>}
          </p>

          <div className="flex flex-wrap gap-2">
            <Btn testid="fp-sync-preview-btn" onClick={openPreview} busy={busy === "preview"} icon={Eye}>Preview Sync</Btn>
            {!readOnly && status === "OFF" && (
              <Btn testid="fp-sync-activate-btn" tone="green" busy={busy === "activate"} icon={Zap}
                onClick={() => act("activate", () => api.post(`/periods/${periodId}/activate-sync`), "Sync activated & Hari Kerja computed")}>Activate Sync</Btn>
            )}
            {!readOnly && status !== "OFF" && (
              <>
                <Btn testid="fp-sync-recompute-btn" tone="blue" busy={busy === "recompute"} icon={RefreshCw} disabled={status === "LOCKED"}
                  onClick={() => act("recompute", () => api.post(`/periods/${periodId}/recompute-sync`), "Hari Kerja recomputed")}>Recompute</Btn>
                {status === "ACTIVE" ? (
                  <Btn testid="fp-sync-lock-btn" tone="amber" busy={busy === "lock"} icon={Lock}
                    onClick={() => act("lock", () => api.post(`/periods/${periodId}/lock-sync`), "Sync locked")}>Lock Attendance</Btn>
                ) : (
                  <Btn testid="fp-sync-unlock-btn" busy={busy === "unlock"} icon={Unlock}
                    onClick={() => act("unlock", () => api.post(`/periods/${periodId}/unlock-sync`), "Unlocked")}>Unlock</Btn>
                )}
                <Btn testid="fp-sync-disable-btn" busy={busy === "disable"} icon={Power}
                  onClick={() => act("disable", () => api.post(`/periods/${periodId}/disable-sync`), "Sync disabled")}>Disable Sync</Btn>
              </>
            )}
            {!readOnly && st.has_snapshot && (
              <Btn testid="fp-sync-restore-btn" tone="red" busy={busy === "restore"} icon={Undo2}
                onClick={() => { if (window.confirm("Restore Hari Kerja to the pre-sync snapshot? Sync will be disabled.")) act("restore", () => api.post(`/periods/${periodId}/restore-snapshot`), "Hari Kerja restored from snapshot"); }}>Restore Snapshot</Btn>
            )}
            <Link to="/integrations/fingerspot" data-testid="fp-sync-integration-link"
              className="flex h-9 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[12.5px] font-medium text-[#0071E3] transition-all hover:bg-white active:scale-[0.98]">
              <ExternalLink className="h-3.5 w-3.5" /> Manage Mapping & Readiness
            </Link>
          </div>
          {status === "LOCKED" && (
            <p className="rounded-xl bg-[#FFF1E6] px-3 py-2 text-[12px] font-medium text-[#B5590F]">
              Period locked — new scans are still processed but Hari Kerja won't change automatically. Unlock then Recompute to apply changes.
            </p>
          )}
          {st.auto_warning && (
            <div data-testid="fp-auto-warning" className="rounded-xl bg-[#FFF1E6] px-3.5 py-2.5">
              <p className="text-[12.5px] font-semibold text-[#B5590F]">{st.auto_warning}</p>
              {(st.auto_review || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {st.auto_review.map((r) => (
                    <span key={r.employee_id} className="num rounded-md bg-white/70 px-1.5 py-0.5 text-[11px] text-[#B5590F]">
                      {r.nama} ({r.nip}) — {String(r.reason).replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent data-testid="fp-sync-preview-dialog" className="max-h-[85vh] gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/80 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl sm:max-w-[860px]">
          <div className="px-5 pt-5">
            <DialogHeader className="text-left">
              <DialogTitle className="text-[18px] font-semibold tracking-tight">Preview Sync — {preview?.label}</DialogTitle>
            </DialogHeader>
            <p className="mt-1 text-[12.5px] text-[#8E8E93]">Proposed Hari Kerja calculation. Nothing is changed until you click Activate / Recompute.</p>
          </div>
          <div className="mt-3 max-h-[62vh] overflow-auto px-5 pb-5">
            <table className="w-full min-w-[760px] text-left text-[12.5px]">
              <thead>
                <tr className="border-y border-black/[0.06] text-[10px] uppercase tracking-wider text-[#A1A1A6]">
                  <th className="py-2 pr-2 font-semibold">Employee</th>
                  <th className="px-2 font-semibold">PIN</th>
                  <th className="px-2 font-semibold">Source</th>
                  <th className="px-2 text-right font-semibold">Now</th>
                  <th className="px-2 text-right font-semibold">Approved</th>
                  <th className="px-2 text-right font-semibold">Adj</th>
                  <th className="px-2 text-right font-semibold">Proposed</th>
                  <th className="px-2 text-right font-semibold">Diff</th>
                  <th className="px-2 text-center font-semibold">Review</th>
                  <th className="px-2 font-semibold">Mapping</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.rows || []).map((r) => (
                  <tr key={r.employee_id} data-testid="fp-sync-preview-row" className="border-b border-black/[0.04]">
                    <td className="py-2 pr-2"><span className="font-medium">{r.nama}</span> <span className="num text-[11px] text-[#8E8E93]">{r.nip}</span></td>
                    <td className="num px-2">{r.fingerspot_pin || "—"}</td>
                    <td className="px-2"><span className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ${r.attendance_source === "FINGERSPOT" ? "bg-[#E9F9EF] text-[#0E8A46]" : "bg-[#F1F1F4] text-[#6E6E73]"}`}>{r.attendance_source}</span></td>
                    <td className="num px-2 text-right">{r.current_hari_kerja}</td>
                    <td className="num px-2 text-right">{r.approved_days}</td>
                    <td className="num px-2 text-right">{r.manual_adjustment}</td>
                    <td className="num px-2 text-right font-semibold">{r.proposed_hari_kerja}</td>
                    <td className={`num px-2 text-right ${r.difference > 0 ? "text-[#0E8A46]" : r.difference < 0 ? "text-[#C8102E]" : "text-[#8E8E93]"}`}>{r.difference > 0 ? "+" : ""}{r.difference}</td>
                    <td className="num px-2 text-center">{r.needs_review_days || 0}</td>
                    <td className="px-2"><span className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ${r.mapping_status === "LINKED" ? "bg-[#E9F9EF] text-[#0E8A46]" : r.mapping_status === "CONFLICT" ? "bg-[#FDE7EC] text-[#C8102E]" : "bg-[#F1F1F4] text-[#6E6E73]"}`}>{r.mapping_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
